import { useEffect, useRef, useState, type FC } from 'react'
import classnames from 'classnames'
import { Icon, Typography } from 'shared/ui'
import {
  TextField,
  makeTextFieldActionTooltip,
  makeTextFieldAutoFocus,
  makeTextFieldBlurOnEnter,
  makeTextFieldTooltip,
  makeTextFieldLoading,
} from 'shared/ui/TextField'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

// A blur this soon after focus is a programmatic focus hand-off (layout code stealing the
// autofocus), not the user leaving the field: keeps the SWR-24198 deep-link boot quiet.
const FOCUS_LEAVE_GRACE_MS = 200

type IPhoneInputProps = {
  value: string
  errorText?: string | null
  onBlurField: (value: string) => void
  onChangeValue: (value: string) => void
  isLoading: boolean
  modalView?: boolean
}

export const PhoneInput: FC<IPhoneInputProps> = ({
  value,
  errorText,
  onBlurField,
  onChangeValue,
  isLoading,
  modalView,
}) => {
  const [isError, setIsError] = useState<string | null>(null)
  const hasTypedRef = useRef(false)
  const focusedAtRef = useRef(0)

  useEffect(() => {
    setIsError(errorText || null)
  }, [errorText])

  const markFocused = () => {
    focusedAtRef.current = Date.now()
  }

  const checkOnBlur = (value: string) => {
    if (!value.length) {
      const isWithinFocusGrace = Date.now() - focusedAtRef.current < FOCUS_LEAVE_GRACE_MS

      if (hasTypedRef.current || !isWithinFocusGrace) {
        setIsError('Phone number is required')
      }
    } else {
      onBlurField(value)
    }
  }

  const checkOnChangeValue = (value: string) => {
    hasTypedRef.current = true
    setIsError(null)
    onChangeValue(value)
  }

  return (
    <div className={styles.field} aria-label={getAriaLabel('Field')}>
      <span className={styles.label} aria-label={getAriaLabel('Field', 'Label')}>
        <div className={styles.icon} aria-label={getAriaLabel('FieldType', 'Phone')}>
          <Icon icon='phone' />
        </div>
        <Typography
          className={styles.name}
          variant='text-input-field-r'
          tag='div'
          ariaLabel={getAriaLabel('Field', 'NamePhone')}
        >
          Phone
        </Typography>
      </span>
      <div className={styles.fieldContent}>
        <TextField
          className={classnames(styles.value, {
            [styles.modalView]: modalView,
          })}
          size='small'
          variant='outline'
          value={value}
          onFocus={markFocused}
          onBlur={checkOnBlur}
          onChange={checkOnChangeValue}
          InputProps={{ placeholder: 'Enter' }}
          rightActions={[makeTextFieldLoading({ loading: isLoading })]}
          mainActions={[
            makeTextFieldActionTooltip({
              color: 'red',
              placement: 'left',
              active: !!isError,
              message: isError ?? undefined,
              onClick: () => setIsError(''),
            }),
            makeTextFieldTooltip(),
            makeTextFieldAutoFocus({ withFocus: true }),
            makeTextFieldBlurOnEnter(),
          ]}
          ariaLabel={getAriaLabel('Field', 'Value')}
        />
      </div>
    </div>
  )
}
